import React from 'react';
import PropTypes from 'prop-types';

import { Dialog } from '../../Modals/Dialog/Dialog';
import { ImageSourceSettings } from '../../ImageAuthoringSettings/ImageSourceSettings/component';
import { MediaCitationSettings } from '../../MediaCitationSettings/component';
import { ImageDescriptionsSettings } from '../../ImageAuthoringSettings/ImageDescriptionsSettings/component';
import { Tabs, TabList, Tab, TabPanel } from '../../Tabs';

import './style.scss';

export class DetailedImageAuthoringModal extends React.PureComponent {
  static propTypes = {
    // Prefix prop names with 'default' because this is an uncontrolled component
    // https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html#recommendation-fully-uncontrolled-component-with-a-key
    defaultSrc: PropTypes.string,
    defaultAltText: PropTypes.string,
    defaultLongDescription: PropTypes.string,
    defaultCitationText: PropTypes.string,
    defaultCitationLink: PropTypes.string,

    hideRemoveButton: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired, // Promise
  };

  static defaultProps = {
    defaultSrc: '',
    defaultAltText: '',
    defaultLongDescription: '',
    defaultCitationText: '',
    defaultCitationLink: '',
    hideRemoveButton: false,
  };

  constructor(props) {
    super(props);

    const {
      defaultSrc,
      defaultAltText,
      defaultLongDescription,
      defaultCitationText,
      defaultCitationLink,
    } = props;

    this.state = {
      showModal: true,

      file: null,

      src: defaultSrc,
      altText: defaultAltText,
      longDescription: defaultLongDescription,
      citationText: defaultCitationText,
      citationLink: defaultCitationLink,

      srcError: null,
      fileError: null,
      altTextError: null,
      longDescriptionError: null,
      citationTextError: null,
      citationLinkError: null,
    };
  }

  onHideClick = () => {
    // begin to fade out
    this.setState({
      showModal: false,
    });
  };

  onSaveClick = () => {
    const { onSave } = this.props;
    const {
      src,
      file,
      altText,
      longDescription,
      citationText,
      citationLink,
    } = this.state;

    const data = {
      src,
      file,
      altText,
      longDescription,
      citationText,
      citationLink,
    };

    if (this.allInputValid()) {
      onSave(data)
        .then(() => {
          this.setState({
            showModal: false,
          });
        })
        .catch((errors) => {
          this.setState({
            srcError: errors.srcError,
            fileError: errors.fileError,
            altTextError: errors.AltTextError,
            longDescriptionError: errors.LongDescriptionError,
            citationTextError: errors.CitationTextError,
            citationLinkError: errors.CitationLinkError,
          });
        });
    } else {
      // if there are errors, pressing the save button won't
      // save and close the modal.
      // TODO: probably good to display message prompting user to correct
      // errors before saving. Design input needed.
    }
  };

  allInputValid = () => {
    const {
      srcError,
      fileError,
      altTextError,
      longDescriptionError,
      citationTextError,
      citationLinkError,
    } = this.state;

    const errors = [
      srcError,
      fileError,
      altTextError,
      longDescriptionError,
      citationTextError,
      citationLinkError,
    ];

    // if all of the errors are null, that means they are all valid
    return errors.filter((errorValue) => !!errorValue).length === 0;
  };

  render() {
    const { onClose, hideRemoveButton } = this.props;
    const {
      showModal,
      src,
      file,
      altText,
      longDescription,
      citationText,
      citationLink,

      srcError,
      fileError,
      altTextError,
      longDescriptionError,
      citationTextError,
      citationLinkError,
    } = this.state;

    const sourceProps = {
      src,
      file,
      altText,

      srcError,
      fileError,

      hideRemoveButton,
      srcOnChange: (value) => {
        this.setState({ src: value });
      },
      fileOnChange: (value) => {
        this.setState({ file: value });
      },
    };

    const descriptionProps = {
      altText,
      longDescription,

      altTextError,
      longDescriptionError,

      altTextOnChange: (value) => {
        this.setState({ altText: value });
      },
      longDescriptionOnChange: (value) => {
        this.setState({ longDescription: value });
      },
    };

    const citationProps = {
      citationText,
      citationLink,

      citationTextError,
      citationLinkError,

      citationTextOnChange: (value) => {
        this.setState({ citationText: value });
      },
      citationLinkOnChange: (value) => {
        this.setState({ citationLink: value });
      },

      citationLinkOnBlur: (event) => {
        const input = event.target;
        const isValid = input.checkValidity(); // uses HTML5 form validation

        this.setState(() => ({
          citationLinkError: isValid ? null : input.validationMessage,
        }));
      },
    };

    const actions = [
      {
        label: 'Save',
        onClick: this.onSaveClick,
        type: 'default',
      },
      {
        label: 'Cancel',
        onClick: this.onHideClick,
        type: 'default',
        left: true,
      },
    ];

    return (
      <Dialog
        show={showModal}
        title="Image information"
        actions={actions}
        dialogClassName="detailed-image-authoring-modal"
        onHide={this.onHideClick}
        onClosed={onClose}
      >
        <Tabs>
          <TabList>
            <Tab>General</Tab>
            <Tab>Accessibility</Tab>
          </TabList>

          <TabPanel>
            <ImageSourceSettings {...sourceProps} />

            <fieldset className="modal-fieldset">
              <legend className="modal-legend">Citation</legend>
              <MediaCitationSettings {...citationProps} />
            </fieldset>
          </TabPanel>

          <TabPanel>
            <ImageDescriptionsSettings {...descriptionProps} />
          </TabPanel>
        </Tabs>
      </Dialog>
    );
  }
}
